import { ReactNode } from "react";

import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { EMPTY_VALUE_PLACEHOLDER, formatShortDate } from "@shared";
import { TFunction } from "i18next";

import { PortalAccountStateDto, PortalAccountStatusResponse, SecondFactorStateDto } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { formatTimestamp } from "@/common/utils/formatTimestamp";
import { useTranslation } from "@/translations";

import { usePortalAccountStateLabels } from "../hooks/usePortalAccountStateLabels";

const stateIcons: Record<PortalAccountStateDto, ReactNode> = {
  [PortalAccountStateDto.NotGranted]: <LockOutlinedIcon color="action" />,
  [PortalAccountStateDto.InvitationSent]: <MarkEmailReadOutlinedIcon color="warning" />,
  [PortalAccountStateDto.Registered]: <VerifiedUserOutlinedIcon color="success" />,
  [PortalAccountStateDto.Locked]: <BlockOutlinedIcon color="error" />,
};

interface Props {
  status: PortalAccountStatusResponse;
  /** Only clients record ID-document details (Ausweisdaten); the volunteer page leaves the rows out. */
  showIdentityDocument?: boolean;
}

/** The "Aktueller Zugriffsstatus" header with the state-dependent detail rows. */
export function PortalAccountStatusCard({ status, showIdentityDocument = false }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const stateLabels = usePortalAccountStateLabels();

  const secondFactorValue = (() => {
    switch (status.secondFactor) {
      case SecondFactorStateDto.Active:
        return secondFactorMethodLabel(status.secondFactorMethod, t);
      case SecondFactorStateDto.Unknown:
        return t("portalAccess.statusRows.secondFactorUnknown");
      default:
        return t("portalAccess.statusRows.secondFactorNone");
    }
  })();

  const registrationValue =
    status.state === PortalAccountStateDto.InvitationSent
      ? t("portalAccess.statusRows.registrationPending")
      : status.registeredAtUtc
        ? `${t("portalAccess.statusRows.registrationCompleted")} · ${formatTimestamp(status.registeredAtUtc, t, language)}`
        : undefined;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        p={2}
        sx={{ backgroundColor: (theme) => theme.palette.fsw["grey-25"] }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {stateIcons[status.state]}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
            {t("portalAccess.currentStatus")}
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.3}>
            {stateLabels[status.state]}
          </Typography>
        </Box>
      </Stack>

      <Stack divider={<Divider />} spacing={2} pt={2}>
        {status.state === PortalAccountStateDto.NotGranted ? (
          <StatusRow
            label={t("portalAccess.statusRows.access")}
            value={t("portalAccess.statusRows.accessNotGranted")}
          />
        ) : (
          <>
            <StatusRow label={t("portalAccess.statusRows.email")} value={status.email ?? ""} monospace />
            {showIdentityDocument && (
              <>
                <StatusRow
                  label={t("portalAccess.statusRows.identityDocumentType")}
                  value={status.identityDocumentType ?? EMPTY_VALUE_PLACEHOLDER}
                />
                <StatusRow
                  label={t("portalAccess.statusRows.identityDocumentNumber")}
                  value={status.identityDocumentNumber ?? EMPTY_VALUE_PLACEHOLDER}
                />
                <StatusRow
                  label={t("portalAccess.statusRows.identityDocumentIssuingAuthority")}
                  value={status.identityDocumentIssuingAuthority ?? EMPTY_VALUE_PLACEHOLDER}
                />
                <StatusRow
                  label={t("portalAccess.statusRows.identityDocumentValidUntil")}
                  value={
                    status.identityDocumentValidUntil
                      ? formatShortDate(status.identityDocumentValidUntil, locales[language])
                      : EMPTY_VALUE_PLACEHOLDER
                  }
                />
              </>
            )}
            {status.invitationSentAtUtc && (
              <StatusRow
                label={t("portalAccess.statusRows.lastSent")}
                value={formatTimestamp(status.invitationSentAtUtc, t, language)}
              />
            )}
            {registrationValue && (
              <StatusRow label={t("portalAccess.statusRows.registration")} value={registrationValue} />
            )}
            {/* Only a registered user can have a second factor — before that there is no Keycloak user. */}
            {(status.state === PortalAccountStateDto.Registered || status.state === PortalAccountStateDto.Locked) && (
              <StatusRow label={t("portalAccess.statusRows.secondFactor")} value={secondFactorValue} />
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Names the identity provider's credential type. An unmapped type is shown raw rather than
 * swallowed, so a second factor introduced at the identity provider is still visible to staff.
 */
function secondFactorMethodLabel(method: string | null, t: TFunction): string {
  switch (method) {
    case "otp":
      return t("portalAccess.secondFactorMethods.otp");
    case "so-email-code":
      return t("portalAccess.secondFactorMethods.email");
    case "so-sms-code":
      return t("portalAccess.secondFactorMethods.sms");
    default:
      return method ?? "";
  }
}

function StatusRow({ label, value, monospace = false }: { label: string; value: string; monospace?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold" sx={monospace ? { fontFamily: "monospace" } : undefined}>
        {value}
      </Typography>
    </Stack>
  );
}
